import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveWorkspace,
    selectActiveWorkspaceId,
    selectIdRenaming,
    selectLegacyWorkspaceTree,
    setIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import * as Icons from "@nova/component-icons"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { selectActivatedFire, selectActivatedFireId } from "features/map/control/selectors"
import packageJson from "../../../../package.json"
import { animation150, navbarHeight, Viewer, zIndex } from "@nova/constants"
import classNames from "classnames"
import { findTagAncestorIds, findTagAncestors } from "features/data-management/workspace/module"
import { ButtonPrimary, ButtonTertiarySquare, IconButton } from "@nova/component-btn"
import { HelpAndResources } from "features/data-management/workspace/TODO-put-in-package/HelpAndResources"
import { NotificationsDrawer } from "panels/notifications/NotificationsDrawer"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { selectUserFullName } from "features/entity-caching/members/UserManagementSlice"
// import { selectWatching } from "features/pubsub/slice"
import { selectUserID, selectUserEmail } from "user/profile/selectors"
import { Tooltip } from "./TODO-put-in-package/Tooltip"
import { ClickAwayListener, ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import {
    FlyoutMenu,
    FlyoutMenuContentItem,
} from "features/data-management/workspace/TODO-put-in-package/FlyoutMenu/FlyoutMenu"
import {
    selectWatching,
    subscribeToMap,
    unsubscribeToMap,
    selectViewers,
} from "features/pubsub/slice"
import { TopBarToolBar } from "./toolbar/TopBarToolbar"
import { TopBarFlyoutLayers } from "./flyout-plugins/TopBarFlyoutLayers"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { updateFire } from "features/entity-caching/fires/thunks"
import { DronesPreview } from "panels/controls/DronesPreview"
import { useUpdateCursorRef } from "tools/events/useCursorProvider"
import { clearSelection, setActiveFeatureId, setActiveLayerId } from "../side-panel/slice.new"
import {
    selectActiveProjectIds,
    selectFavoritesData,
} from "features/data-management/workspace/context/selectors"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { NovaRouteTree, useRouter } from "pages"
import { GetScopedTo } from "@nova/react-router"
import { getSnapshotState, showGlobalModal } from "runlevel/actions"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { getColorFromId } from "@nova/util-strings"
import { logout } from "user/authentication/thunks"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"

/** Does not include self. */
const MAX_VIEWERS = 4

const [major, minor, patch] = packageJson.version.split(".")
const formatVersion = () => {
    let version = `v${major}.${minor}`
    if (patch && patch !== "0") {
        version += `.${patch}`
    }
    return version
}

enum ActiveMenu {
    Folders = 1,
    Help = 2,
    Logout = 3,
    Workspace = 4,
}

const id = "top-bar"

const TopBar = memo(() => {
    const dispatch = useAppDispatch()
    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const activeProject = useAppSelector(selectActivatedFire)
    const { isEditor } = useWorkspacePermissions(activeProject?.id || "")
    const workspaceFolderTree = useAppSelector(selectLegacyWorkspaceTree)
    const ancestors = useMemo(
        () =>
            workspaceFolderTree && activeProject?.tagId
                ? findTagAncestors(workspaceFolderTree, activeProject.tagId)
                : [],
        [workspaceFolderTree, activeProject?.tagId]
    )
    const fullName = useAppSelector(selectUserFullName())
    const viewerBeingWatched = useAppSelector(selectWatching())
    const viewers = useAppSelector(selectViewers())
    const userId = useAppSelector(selectUserID)
    const userEmail = useAppSelector(selectUserEmail)
    const [activeMenu, setActiveMenu] = useState<ActiveMenu>()
    const idRenaming = useAppSelector(selectIdRenaming())

    const handleClickViewer = useCallback(
        (viewer?: Viewer) => {
            if (viewerBeingWatched?.id === viewer?.id) {
                dispatch(unsubscribeToMap())
                return
            }
            if (viewer) {
                dispatch(clearSelection())
                dispatch(setActiveLayerId({ id: undefined }))
                dispatch(setActiveFeatureId({ feature: undefined }))
                dispatch(subscribeToMap(viewer))
            }
        },
        [viewerBeingWatched, dispatch]
    )

    const handleShare = useCallback(() => {
        if (isEditor) {
            dispatch(initializeCenterModalForComponentAction(ModalNames.InviteEverywhereModal))
        } else {
            dispatch(initializeCenterModalForComponentAction(ModalNames.RequestAccessModal))
        }
        dispatch(showGlobalModal())
    }, [isEditor, dispatch])

    const router = useRouter("/workspace/:workspaceId")
    const flyoutContentItems: FlyoutMenuContentItem[] = useMemo(
        () =>
            ancestors.map((folder) => ({
                id: folder.id,
                title: folder.name,
                onClick: () => {
                    if (!workspaceFolderTree || !activeWorkspace) return
                    const folderIds = findTagAncestorIds(workspaceFolderTree, folder.id)
                    router.navigate({ path: "/explore/ws/*", splat: folderIds.join("/") })
                },
            })),
        [activeWorkspace, ancestors, router, workspaceFolderTree]
    )

    const projectTitleInputRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
        if (idRenaming === "project-view-project-title") projectTitleInputRef.current?.focus()
    }, [idRenaming])

    useUpdateCursorRef(id)

    const isTakingWorkspaceSnapshot = !!getSnapshotState()

    return (
        <div
            id={id}
            className="w-full flex"
            style={{ height: navbarHeight, zIndex: zIndex.Navbar }}
        >
            <>
                {/* Navigation */}
                <div className="flex-[3_3_0%] h-full flex ml-3 mr-4 gap-3 items-center">
                    {/* Nova Logo */}
                    <div className="relative">
                        <div
                            className={classNames(
                                "flex item-center hover:bg-gray-700 hover:shadow-elevation-5 cursor-pointer py-1 rounded-md transition-colors pl-1 group",
                                {
                                    "bg-gray-700 shadow-elevation-5":
                                        activeMenu === ActiveMenu.Workspace,
                                }
                            )}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => {
                                setActiveMenu((prev) =>
                                    prev === ActiveMenu.Workspace ? undefined : ActiveMenu.Workspace
                                )
                            }}
                        >
                            <div className="relative">
                                <div className="absolute inset-0">
                                    <Icons.Icon Icon={Icons.NovaLogoStripped} />
                                </div>
                                <Icons.Icon
                                    Icon={Icons.NovaLogo}
                                    iconClassName={classNames(
                                        "group-hover:opacity-100 transition-opacity",
                                        {
                                            "opacity-0": activeMenu !== ActiveMenu.Workspace,
                                        }
                                    )}
                                />
                            </div>
                            <Icons.Icon
                                Icon={Icons.ChevronUp}
                                iconClassName={classNames(
                                    "text-white-500 m-0 transition-transform",
                                    {
                                        "rotate-180": activeMenu === ActiveMenu.Workspace,
                                    }
                                )}
                            />
                        </div>
                        {activeMenu === ActiveMenu.Workspace && (
                            <div className="absolute -left-1 top-full translate-y-1 bg-gray-900 border border-white-200 py-4 px-2 rounded-lg">
                                <WorkspaceFlyout
                                    onBlur={() => {
                                        setActiveMenu(undefined)
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Breadcrumbs */}
                    {activeWorkspace && activeProject && (
                        <div className="flex gap-2 items-center">
                            {/* Workspace Title */}
                            <router.Link to={{ path: "/" }}>
                                <h5
                                    className={classNames(
                                        animation150,
                                        "text-gray-300 hover:text-gray-0 cursor-pointer select-none truncate max-w-[150px]"
                                    )}
                                >
                                    {activeWorkspace.name}
                                </h5>
                            </router.Link>

                            {/* Chevron */}
                            <Icons.SmallIcon Icon={Icons.ChevronRight} className="text-gray-500" />

                            {/* Parent Folder(s) */}
                            {ancestors.length > 0 && (
                                <div
                                    className={classNames(
                                        "relative text-gray-300 hover:text-gray-0 cursor-pointer flex items-center",
                                        animation150
                                    )}
                                >
                                    {ancestors.length === 1 ? (
                                        // Parent Name (if no further ancestry)
                                        <router.Link
                                            to={{ path: "/explore/ws/*", splat: ancestors[0].id }}
                                        >
                                            <h5 className="mr-2 select-none truncate max-w-[150px]">
                                                {ancestors[0].name}
                                            </h5>
                                        </router.Link>
                                    ) : (
                                        // Folder and Dropdown Icon for Multiple Folders
                                        ancestors.length > 1 && (
                                            <div
                                                className="flex"
                                                onClick={() =>
                                                    setActiveMenu((prev) =>
                                                        prev === ActiveMenu.Folders
                                                            ? undefined
                                                            : ActiveMenu.Folders
                                                    )
                                                }
                                            >
                                                <Icons.Icon Icon={Icons.Folder} />
                                                <Icons.Icon Icon={Icons.ArrowDropdown} />
                                            </div>
                                        )
                                    )}

                                    {/* Chevron */}
                                    <Icons.SmallIcon
                                        Icon={Icons.ChevronRight}
                                        className="text-gray-500"
                                    />

                                    {/* Flyout Menu */}
                                    {activeMenu === ActiveMenu.Folders && (
                                        <div
                                            className="absolute -left-1.5 -translate-x-1/2"
                                            style={{ top: "calc(100% + 0.5rem)" }}
                                        >
                                            <FlyoutMenu
                                                title="Folders"
                                                contentItems={flyoutContentItems}
                                                onBlur={() => setActiveMenu(undefined)}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Project Title */}
                            <RenamableText
                                ref={projectTitleInputRef}
                                id="project-view-project-title"
                                title={activeProject.name}
                                Tag="h5"
                                className="text-gray-0 cursor-text"
                                isRenaming={idRenaming === "project-view-project-title"}
                                shouldHighlightOnHover={isEditor}
                                isFullWidth={false}
                                onClick={
                                    isEditor
                                        ? () =>
                                              dispatch(setIdRenaming("project-view-project-title"))
                                        : undefined
                                }
                                onConfirmRenaming={(_, name) => {
                                    dispatch(
                                        updateFire({
                                            id: activeProject.id,
                                            updates: { name },
                                        })
                                    )
                                    dispatch(setIdRenaming(undefined))
                                }}
                                onCancelRenaming={() => dispatch(setIdRenaming(undefined))}
                            />
                        </div>
                    )}
                </div>

                {/* Toolbar */}
                {!isTakingWorkspaceSnapshot && <TopBarToolBar />}

                <div className="flex items-center gap-2 justify-end flex-[3_3_0%] h-full mr-2">
                    <NotificationsDrawer
                        direction="right"
                        renderTrigger={(hasUnreadNotifications) => (
                            <IconButton className="text-gray-100 py-1">
                                <Icons.SmallIcon
                                    Icon={
                                        hasUnreadNotifications
                                            ? Icons.NotificationStatus
                                            : Icons.Notification
                                    }
                                />
                            </IconButton>
                        )}
                    />

                    {activeProject && userId && (
                        <div className="relative">
                            <div className="flex gap-1">
                                <TopBarFlyoutLayers />
                                <DronesPreview />

                                {/* Extra Viewers (+X) Icon */}
                                {viewers.length > MAX_VIEWERS && (
                                    <div className="w-8 h-8 bg-gray-300 rounded-lg flex justify-center items-center relative group/tooltip">
                                        <h5 className="text-gray-700">{`+${viewers.length - MAX_VIEWERS}`}</h5>
                                        <Tooltip
                                            rowGapInRem={0.125}
                                            title={viewers
                                                .slice(MAX_VIEWERS)
                                                .map((viewer) => viewer.name)
                                                .join("\n")}
                                        />
                                    </div>
                                )}
                                {/* The First n Viewers' Icons */}
                                {viewers.map((viewer, i) =>
                                    i < MAX_VIEWERS ? (
                                        <div
                                            key={i}
                                            className="relative group/tooltip"
                                            onClick={() => handleClickViewer(viewer)}
                                        >
                                            <div
                                                className={classNames(
                                                    "w-8 min-w-[32px] aspect-square rounded-lg overflow-hidden cursor-pointer relative",
                                                    "outline transition-colors",
                                                    {
                                                        "outline-transparent hover:outline-white-500":
                                                            viewer.id !== viewerBeingWatched?.id,
                                                        "outline-white-1000":
                                                            viewer.id === viewerBeingWatched?.id,
                                                    }
                                                )}
                                                style={{
                                                    background: getColorFromId(viewer.userId),
                                                }}
                                            >
                                                <div className="absolute inset-0.5 rounded-md overflow-hidden">
                                                    <ProfilePhoto
                                                        userId={viewer.userId}
                                                        fullName={viewer.name}
                                                    />
                                                </div>
                                            </div>
                                            {viewer.id === viewerBeingWatched?.id && (
                                                <div
                                                    className="absolute bg-blue-500 w-full h-0.5"
                                                    style={{ top: "calc(100% + 0.375rem)" }}
                                                />
                                            )}
                                            <Tooltip hoverable>
                                                <UserProfileModalTrigger userId={viewer.userId}>
                                                    <small className="group-hover/UserProfileModalTrigger:underline text-gray-50">
                                                        {viewer.name}
                                                    </small>
                                                </UserProfileModalTrigger>
                                            </Tooltip>
                                        </div>
                                    ) : null
                                )}

                                {/* Logged-in User Icon */}
                                <div
                                    className="relative group/tooltip"
                                    onClick={() =>
                                        setActiveMenu((prev) =>
                                            prev === ActiveMenu.Logout
                                                ? undefined
                                                : ActiveMenu.Logout
                                        )
                                    }
                                >
                                    <div
                                        className="w-8 min-w-[32px] aspect-square rounded-lg overflow-hidden cursor-pointer relative"
                                        style={{
                                            background: getColorFromId(userId),
                                        }}
                                    >
                                        <div className="absolute inset-0.5 rounded-md overflow-hidden">
                                            <ProfilePhoto self />
                                        </div>
                                    </div>
                                    {fullName && activeMenu !== ActiveMenu.Logout && (
                                        <Tooltip hoverable>
                                            <UserProfileModalTrigger userId={userId}>
                                                <small className="group-hover/UserProfileModalTrigger:underline text-gray-50">
                                                    {fullName} (you)
                                                </small>
                                            </UserProfileModalTrigger>
                                        </Tooltip>
                                    )}
                                    {/* Logout Menu */}
                                    {activeMenu === ActiveMenu.Logout && (
                                        <ClickAwayListener
                                            onClickAway={() => setActiveMenu(undefined)}
                                        >
                                            <div
                                                className="absolute left-1/2 -translate-x-1/2 bg-gray-900 border border-white-300 rounded-xl shadow-card p-2 gap-2 flex flex-col items-center"
                                                style={{ top: "calc(100% + 0.25rem)" }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <UserProfileModalTrigger userId={userId}>
                                                    <div className="flex flex-col items-center max-h-10">
                                                        <h5 className="text-gray-50 select-none group-hover/UserProfileModalTrigger:underline">
                                                            {fullName}
                                                        </h5>
                                                        <h5 className="text-gray-400">
                                                            {userEmail}
                                                        </h5>
                                                    </div>
                                                </UserProfileModalTrigger>
                                                <ButtonPrimary
                                                    LeftIcon={Icons.Exit}
                                                    title="Logout"
                                                    onClick={() => dispatch(logout())}
                                                    className="w-full"
                                                />
                                            </div>
                                        </ClickAwayListener>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Help Button */}
                    <div className="relative group/tooltip">
                        <ButtonTertiarySquare
                            Icon={Icons.Help}
                            onClick={() =>
                                setActiveMenu((prev) =>
                                    prev === ActiveMenu.Help ? undefined : ActiveMenu.Help
                                )
                            }
                        />
                        {activeMenu === ActiveMenu.Help && (
                            <div
                                className="absolute flex w-[13rem] right-1/2"
                                style={{
                                    transform: "translateX(calc(50% - 0.5rem))",
                                    top: "calc(100% + 0.25rem)",
                                }}
                            >
                                <HelpAndResources
                                    title={formatVersion()}
                                    className="w-[13rem]"
                                    onBlur={() => setActiveMenu(undefined)}
                                />
                            </div>
                        )}
                        {!activeMenu && <Tooltip title="Support" />}
                    </div>

                    {/* Share Button */}
                    {!isTakingWorkspaceSnapshot && (
                        <ButtonPrimary
                            title={isEditor ? "Invite" : "Ask to Edit"}
                            onClick={handleShare}
                        />
                    )}
                </div>
            </>
        </div>
    )
})
TopBar.displayName = "TopBar"
export { TopBar }

function WorkspaceFlyout({ onBlur }: { onBlur: () => void }) {
    const activeProjectIds = useAppSelector(selectActiveProjectIds)
    const { sortedRecentProjectIds } = useAppSelector(selectFavoritesData)
    const trimmedActiveProjectIds = useMemo(() => activeProjectIds.slice(0, 3), [activeProjectIds])
    const trimmedRecentProjectIds = useMemo(
        () => sortedRecentProjectIds.slice(0, 3),
        [sortedRecentProjectIds]
    )
    return (
        <ClickAwayListenerV2 onClickAway={onBlur} className="flex flex-col gap-2">
            <div className="text-white pl-4">
                <Icons.Icon Icon={Icons.NovaLogoInverted} />
            </div>
            <div className="flex flex-col gap-px">
                <small className="xs text-white-500 px-4 py-1.5">NOVA</small>
                <WorkspaceFlyoutItem Icon={Icons.Home} title="Home" to={{ path: "/" }} />
                <WorkspaceFlyoutItem
                    Icon={Icons.CaptivePortal}
                    title="Overview"
                    to={{ path: "/overview" }}
                />
                <WorkspaceFlyoutItem
                    Icon={Icons.Folder}
                    title="Workspace"
                    to={{ path: "/explore/ws" }}
                />
                <WorkspaceFlyoutItem
                    Icon={Icons.Graph7}
                    title="Pilot Tracking"
                    to={{ path: "/fleet" }}
                />
            </div>
            {!!trimmedActiveProjectIds.length && (
                <div className="flex flex-col gap-px">
                    <div className="px-4 py-1.5 flex gap-3 items-center">
                        <div className="relative w-5 aspect-square flex justify-center items-center">
                            <div className="absolute w-2.5 aspect-square bg-green-500/40 rounded-full animate-ping" />
                            <div className="relative w-2.5 aspect-square bg-green-500/20 flex justify-center items-center rounded-full">
                                <div className="w-1.5 aspect-square bg-green-500 rounded-full" />
                            </div>
                        </div>
                        <small className="xs text-white-500">ACTIVE PROJECTS</small>
                    </div>
                    {trimmedActiveProjectIds.map((projectId, i) => (
                        <WorkspaceFlyoutProject key={projectId} id={projectId} index={i} />
                    ))}
                </div>
            )}
            {!!trimmedRecentProjectIds.length && (
                <div className="flex flex-col gap-px">
                    <small className="xs text-white-500 px-4 py-1.5">RECENT PROJECTS</small>
                    {trimmedRecentProjectIds.map((projectId, i) => (
                        <WorkspaceFlyoutProject key={projectId} id={projectId} index={i} />
                    ))}
                </div>
            )}
        </ClickAwayListenerV2>
    )
}

function WorkspaceFlyoutProject({ id, index }: { id: string; index: number }) {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const activeProjectId = useAppSelector(selectActivatedFireId)
    const allProjectsById = useAppSelector(selectAllFires)
    const project = allProjectsById[id]
    if (!project || !workspaceId) return null
    return (
        <WorkspaceFlyoutItem
            Icon={flightIcons[index % flightIcons.length]}
            title={project.name || "Project"}
            to={{ path: "/project/:projectId", params: { projectId: project.id } }}
            isCurrent={id === activeProjectId}
        />
    )
}

function FlightIcon({ color }: { color: string }) {
    return (
        <div className="w-5 aspect-square flex justify-center items-center">
            <div
                className="w-4 h-1 rounded-full -rotate-45"
                style={{
                    background: color,
                }}
            />
        </div>
    )
}

const flightIcons = [
    () => <FlightIcon color={"#578FA2"} />, // pubsub-slate-blue
    () => <FlightIcon color={"#E29D51"} />, // pubsub-gold-apricot
    () => <FlightIcon color={"#9AA077"} />, // pubsub-olive-sage
]

function WorkspaceFlyoutItem({
    Icon,
    title,
    to,
    isCurrent = false,
}: {
    Icon: Icons.IconComponent
    title: string
    to: GetScopedTo<NovaRouteTree, "/workspace/:workspaceId">
    isCurrent?: boolean
}) {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <router.Link to={to}>
            <div className="px-4 py-1.5 flex gap-3 items-center group hover:bg-blue-500 transition-colors cursor-pointer rounded-lg w-[268px]">
                <Icons.SmallIcon
                    Icon={Icon}
                    iconClassName="text-white-400 group-hover:text-gray-900 transition-colors"
                />
                <div className="h-full flex justify-between items-center w-full shrink min-w-0">
                    <div className="text-white group-hover:text-gray-900 transition-colors shrink min-w-0 truncate">
                        {title}
                    </div>
                    {isCurrent && (
                        <div className="h-full px-2 rounded-full bg-white-100 text-blue-500 group-hover:text-black-600 transition-colors">
                            Current
                        </div>
                    )}
                </div>
            </div>
        </router.Link>
    )
}
